export const env = import.meta.env.VITE_ENV || 'develop';

export function debounce<F extends (query: string) => void>(
	fn: F,
	delay: number,
): (query: string) => void {
	let timeoutId: ReturnType<typeof setTimeout>;

	return (query: string) => {
		if (timeoutId) {
			clearTimeout(timeoutId);
		}
		timeoutId = setTimeout(() => {
			fn(query);
		}, delay);
	};
}

export { formatChartNumber } from './chartUtils';
export {
	extractDocumentName,
	type FileIconType,
	fileToBase64,
	getDisplayableImageUrl,
	getFileIcon,
} from './fileUtils';

export const INACTIVITY_TIMEOUT = (() => {
	switch (env) {
		case 'develop':
			return 30 * 60 * 1000; // 30 minutes
		case 'stage':
			return 30 * 60 * 1000; // 30 minutes

		case 'preprod':
			return 30 * 60 * 1000; // 15 minutes
		case 'prod':
			return 30 * 60 * 1000; // 15 minutes
		default:
			return 30 * 60 * 1000; // 30 minutes
	}
})();

export { transformBVRecordToPreview } from './bvUtils';
export { convertToISODateFormat, formatDate, formatDateTime } from './dateUtils';
export {
	formatProductError,
	formatValidationError,
	parseUserFriendlyError,
} from './errorUtils';
export { handleNumericInputChange } from './formValidationUtils';

type InfiniteLike = {
	fetchNextPage: () => any;
	hasNextPage?: boolean;
	isFetchingNextPage?: boolean;
	isLoading?: boolean;
	isFetching?: boolean;
};

export const makeInfiniteScrollHandler = <T extends HTMLElement>(
	query: InfiniteLike,
	opts?: { thresholdPx?: number; throttleMs?: number; enabled?: boolean }
) => {
	const thresholdPx = opts?.thresholdPx ?? 180;
	const throttleMs = opts?.throttleMs ?? 200;
	const enabled = opts?.enabled ?? true;

	let lastCall = 0;

	return (e: React.UIEvent<T>) => {
		if (!enabled) return;

		const now = Date.now();
		if (now - lastCall < throttleMs) return;
		lastCall = now;

		if (query.isLoading) return;
		if (query.isFetchingNextPage) return;
		if (query.hasNextPage === false) return;

		const el = e.currentTarget;
		const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - thresholdPx;
		if (!nearBottom) return;

		query.fetchNextPage();
	};
}

export const autoFillIfNotScrollable = (el: HTMLElement | null, query: InfiniteLike) => {
	if (!el) return;
	if (query.hasNextPage === false) return;
	if (query.isLoading || query.isFetchingNextPage) return;

	const notScrollable = el.scrollHeight <= el.clientHeight + 8;
	if (notScrollable) query.fetchNextPage();
}


const avatarColors = ["red", "green", "blue", "orange"] as const;

export const getRandomColor = (username: string) => {
  if (!username) return avatarColors[0];

  const firstLetter = username.trim().charAt(0).toUpperCase();
  const charCode = firstLetter.charCodeAt(0);

  const index = charCode % avatarColors.length;

  return avatarColors[index];
};

export function removeInvoicePrefix(data: any): any {
  if (Array.isArray(data)) {
    return data.map(item => removeInvoicePrefix(item));
  }
  
  if (data && typeof data === 'object') {
    return Object.keys(data).reduce((acc, key) => {
      // Remove 'invoice_' prefix if it exists
      const newKey = key.startsWith('invoice_') ? key.replace('invoice_', '') : key;
      
      // Recursively process nested objects
      acc[newKey] = removeInvoicePrefix(data[key]);
      
      return acc;
    }, {} as any);
  }
  
  return data;
}